import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { Button } from './ui/button';
import { mockData } from '../mock';

// WhatsApp number in international format (UK: 07950886606 -> 447950886606)
const WHATSAPP_NUMBER = '447950886606';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=Hi%20T%26M%20Construction%2C%20I%27d%20like%20to%20enquire%20about%20your%20services.`;

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Reset navbar visibility when route changes
  useEffect(() => {
    setIsNavVisible(true);
    setLastScrollY(0);
  }, [location.pathname]);

  // Handle scroll to show/hide navbar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 100) {
        // Always show navbar at top of page
        setIsNavVisible(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down - hide navbar
        setIsNavVisible(false);
      } else {
        // Scrolling up - show navbar
        setIsNavVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/projects', label: 'Projects' },
    { path: '/before-after', label: 'Before & After' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/blog', label: 'Blog' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top Contact Bar */}
      <div className={`bg-black text-white py-2 fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${isNavVisible ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between text-sm">
            {/* Left - Email & Phone */}
            <div className="flex items-center space-x-6">
              <a href={`mailto:${mockData.company.email}`} className="flex items-center space-x-2 hover:text-orange-500 transition-colors">
                <LucideIcons.Mail className="w-4 h-4 text-orange-500" />
                <span className="font-light hidden sm:inline">{mockData.company.email}</span>
              </a>
              <a href={`tel:${mockData.company.phone}`} className="flex items-center space-x-2 hover:text-orange-500 transition-colors">
                <LucideIcons.Phone className="w-4 h-4 text-orange-500" />
                <span className="font-light hidden sm:inline">{mockData.company.phone}</span>
              </a>
            </div>
            {/* Right - Social Media */}
            <div className="flex items-center space-x-4">
              <a href={mockData.company.social.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors" title="LinkedIn">
                <LucideIcons.Linkedin className="w-4 h-4" />
              </a>
              <a href={mockData.company.social.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors" title="Facebook">
                <LucideIcons.Facebook className="w-4 h-4" />
              </a>
              <a href={mockData.company.social.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors" title="Instagram">
                <LucideIcons.Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header with Logo and Navigation */}
      <header className={`fixed top-8 left-0 right-0 z-50 bg-white shadow-lg border-b-4 border-orange-600 transition-transform duration-300 ${isNavVisible ? 'translate-y-0' : '-translate-y-[calc(100%+2rem)]'}`}>
        <nav className="container mx-auto px-6 py-3">
          {/* Logo - Centered */}
          <div className="flex justify-center mb-3">
            <Link to="/" className="transform hover:scale-105 transition-transform cursor-pointer">
              <img 
                src="https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/luyat48h_13A23097-B5B2-4DBF-90E7-8284653A1174.png" 
                alt="T&M Builders Suffolk - Construction Services"
                className="h-28 w-auto object-contain"
              />
            </Link>
          </div>
          
          {/* Desktop Navigation - Centered under logo */}
          <div className="hidden lg:flex items-center justify-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-md text-sm font-light uppercase tracking-wider transition-all ${
                  location.pathname === link.path
                    ? 'bg-orange-600 text-white'
                    : 'text-gray-700 hover:bg-orange-100 hover:text-orange-600'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/contact">
              <Button className="ml-4 bg-orange-600 hover:bg-orange-700 text-white font-light uppercase tracking-wider px-6 py-2">
                <LucideIcons.Phone className="w-4 h-4 mr-2" />
                GET QUOTE
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <LucideIcons.X className="w-6 h-6 text-gray-700" />
            ) : (
              <LucideIcons.Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-3 px-4 rounded-lg transition-all font-light ${
                    location.pathname === link.path
                      ? 'bg-orange-600 text-white'
                      : 'text-gray-700 hover:bg-orange-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white mt-2 font-light">
                  Get a Quote
                </Button>
              </Link>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Floating WhatsApp Button */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 group"
        aria-label="Chat on WhatsApp"
      >
        <WhatsAppIcon className="w-8 h-8" />
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-black text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
          Chat with us!
        </span>
      </a>

      {/* Construction-Style Footer */}
      <footer className="steel-gradient text-white py-12 border-t-4 border-orange-600 relative overflow-hidden">
        <div className="construction-pattern absolute inset-0 opacity-5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <Link to="/" className="inline-block hover:scale-105 transition-transform cursor-pointer">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/luyat48h_13A23097-B5B2-4DBF-90E7-8284653A1174.png" 
                    alt="T&M Builders Suffolk Logo"
                    className="h-24 w-auto object-contain"
                  />
                </Link>
              </div>
              <p className="text-gray-400 mb-4">
                Delivering quality construction and renovation services across Suffolk, Essex, and London.
              </p>
              <div className="flex space-x-4">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-400 transition-colors" title="Chat on WhatsApp">
                  <WhatsAppIcon className="w-5 h-5" />
                </a>
                <a href={mockData.company.social.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-600 transition-colors">
                  <LucideIcons.Linkedin className="w-5 h-5" />
                </a>
                <a href={mockData.company.social.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-600 transition-colors">
                  <LucideIcons.Facebook className="w-5 h-5" />
                </a>
                <a href={mockData.company.social.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-600 transition-colors">
                  <LucideIcons.Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-orange-600 transition-colors">Home</Link></li>
                <li><Link to="/about" className="hover:text-orange-600 transition-colors">About</Link></li>
                <li><Link to="/services" className="hover:text-orange-600 transition-colors">Services</Link></li>
                <li><Link to="/projects" className="hover:text-orange-600 transition-colors">Projects</Link></li>
                <li><Link to="/before-after" className="hover:text-orange-600 transition-colors">Before & After</Link></li>
                <li><Link to="/testimonials" className="hover:text-orange-600 transition-colors">Testimonials</Link></li>
                <li><Link to="/faq" className="hover:text-orange-600 transition-colors">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Services</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Kitchen Renovations</li>
                <li>Bathroom Installations</li>
                <li>Extensions & Lofts</li>
                <li>Property Refurbishment</li>
                <li>Painting & Decorating</li>
                <li>Landscaping</li>
                <li>Cleaning Services</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Contact</h3>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center space-x-2">
                  <LucideIcons.Phone className="w-4 h-4" />
                  <a href={`tel:${mockData.company.phone}`} className="hover:text-orange-600 transition-colors">{mockData.company.phone}</a>
                </li>
                <li className="flex items-center space-x-2">
                  <WhatsAppIcon className="w-4 h-4 text-green-500" />
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-green-400 transition-colors">WhatsApp Us</a>
                </li>
                <li className="flex items-center space-x-2">
                  <LucideIcons.Mail className="w-4 h-4" />
                  <a href={`mailto:${mockData.company.email}`} className="hover:text-orange-600 transition-colors">{mockData.company.email}</a>
                </li>
                <li className="flex items-center space-x-2">
                  <LucideIcons.MapPin className="w-4 h-4" />
                  <span>{mockData.company.address}</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-400">
              <div>
                <p className="mb-2"><strong className="text-gray-300">Trading Name:</strong> {mockData.company.tradingName}</p>
                <p className="mb-2"><strong className="text-gray-300">Registered Company:</strong> {mockData.company.registeredName}</p>
                <p className="mb-2"><strong className="text-gray-300">Company Number:</strong> {mockData.company.companyNumber}</p>
                <p className="mb-2"><strong className="text-gray-300">Place of Registration:</strong> {mockData.company.placeOfRegistration}</p>
                <p><strong className="text-gray-300">VAT Number:</strong> {mockData.company.vatNumber}</p>
              </div>
              <div className="text-center md:text-right">
                <div className="flex justify-center md:justify-end space-x-4 mb-4">
                  <Link to="/terms" className="hover:text-orange-600 transition-colors">Terms & Conditions</Link>
                  <span>|</span>
                  <Link to="/privacy" className="hover:text-orange-600 transition-colors">Privacy Policy</Link>
                  <span>|</span>
                  <Link to="/admin/login" className="hover:text-orange-600 transition-colors" data-testid="staff-login-link">Staff</Link>
                </div>
                <p className="text-gray-400">&copy; {new Date().getFullYear()} {mockData.company.registeredName}. All rights reserved.</p>
              </div>
            </div>
            <div className="text-center mt-6 pt-6 border-t border-gray-800">
              <p className="text-orange-500 font-medium tracking-wide">
                <span>·Construction</span>
                <span className="mx-3">·Renovation</span>
                <span>·Maintenance</span>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;